import { api } from './fetchHelpers';
import {
  BibliographyEntry,
  Dimension,
  PrismElement,
  AtlasArticle,
  CitationIndex,
  AtlasContext,
  AtlasPosthocTagMap,
} from '../types/atlas';
import { AtlasUpdate } from '../store/atlasStore';
import { SparkProjectOverview, SparkProjectDetails, SparkFlow, SparkCreative, SparkIteration, SparkJob, SparkSuggestedResource, SparkCreativeType, SparkAttachment, TagFacetDimension, SparkLibraryItem, SparkLibrarySearchResponse, FocusSearchQuestion, SparkFormOverlayEntry } from '../features/spark/types/sparkTypes';
import { ImportPreviewResponse, ImportCommitResponse } from '../features/spark/types/importTypes';
import { User } from '../types/user';

// Default thumbnail parameters for images rendered in lists, cards, etc.
export const DEFAULT_THUMB_WIDTH = 320;
export const DEFAULT_THUMB_QUALITY = 70;

type AssetUrlOptions = {
  full?: boolean;
  w?: number;
  h?: number;
  q?: number;
  fit?: 'contain' | 'cover';
  fmt?: 'jpeg' | 'png' | 'webp';
};

export class AtlasClient {
  async getDimensionsAndElements(): Promise<Dimension[]> {
    const { data } = await api.get<Dimension[]>('/prism/dimensions');
    return data;
  }

  async createElement(elementData: { dimension_fqn: string; name: string; description: string }): Promise<PrismElement> {
    const { data } = await api.post<PrismElement>('/prism/elements', elementData);
    return data;
  }

  async updateElement(elementFqn: string, elementData: { name: string; description: string }): Promise<PrismElement> {
    const { data } = await api.put<PrismElement>(`/prism/elements/${encodeURIComponent(elementFqn)}`, elementData);
    return data;
  }

  async uploadElementImage(elementFqn: string, file: File): Promise<PrismElement> {
    const formData = new FormData();
    formData.append('file', file);
    
    const { data } = await api.post<PrismElement>(
      `/prism/elements/${encodeURIComponent(elementFqn)}/images/upload`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      }
    );
    return data;
  }

  async removeElementImage(elementFqn: string): Promise<PrismElement> {
    const { data } = await api.delete<PrismElement>(`/prism/elements/${encodeURIComponent(elementFqn)}/images/remove`);
    return data;
  }

  async activateElement(elementFqn: string): Promise<PrismElement> {
    const { data } = await api.post<PrismElement>(`/prism/elements/${encodeURIComponent(elementFqn)}/activate`);
    return data;
  }

  async deactivateElement(elementFqn: string): Promise<PrismElement> {
    const { data } = await api.post<PrismElement>(`/prism/elements/${encodeURIComponent(elementFqn)}/deactivate`);
    return data;
  }

  async downloadArticleExport(article_fqn: string, export_type: 'docx' | 'pptx'): Promise<Blob> {
    const encodedFqn = encodeURIComponent(article_fqn);
    let acceptHeader = '';
    if (export_type === 'docx') {
      acceptHeader = 'application/vnd.openxmlformats-officedocument.wordprocessingml.document';
    } else if (export_type === 'pptx') {
      acceptHeader = 'application/vnd.openxmlformats-officedocument.presentationml.presentation';
    }

    const response = await api.get<Blob>(`/atlas/articles/${encodedFqn}/export/${export_type}`, {
      responseType: 'blob',
      headers: {
        'Accept': acceptHeader
      }
    });
    return response.data;
  }

  async downloadBriefingExport(projectFqn: string): Promise<Blob> {
    const encodedFqn = encodeURIComponent(projectFqn);
    const response = await api.get<Blob>(`/spark/projects/${encodedFqn}/briefing/export/docx`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'
      }
    });
    return response.data;
  }

  async uploadBriefingDocx(projectFqn: string, file: File): Promise<{ markdown: string }> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    formData.append('project_fqn', projectFqn);
    const { data } = await api.post<{ markdown: string }>('/spark/briefing/upload/docx', formData);
    return data;
  }

  async getArticleInfographicExport(article_fqn: string): Promise<{ export_id: string; filename: string }> {
    const encodedFqn = encodeURIComponent(article_fqn);
    const { data } = await api.get<{ export_id: string; export_type: string; filename: string }>(
      `/atlas/articles/${encodedFqn}/export/png`
    );
    return data;
  }

  getArticleExportFileUrl(article_fqn: string, export_id: string, filename: string, type: string = 'html'): string {
    return `/api/atlas/articles/${encodeURIComponent(article_fqn)}/exports/${export_id}/files/${encodeURIComponent(filename)}?type=${type}`;
  }

  async downloadResourcePdf(resource_fqn: string): Promise<Blob> {
    const encodedFqn = encodeURIComponent(resource_fqn);
    // Call the new endpoint added in the assets mixin
    const response = await api.get<Blob>(`/atlas/assets/${encodedFqn}/pdf`, {
      responseType: 'blob',
      headers: {
        'Accept': 'application/pdf'
      }
    });
    return response.data;
  }

  async resolveContentStoreItemUrl(itemId: string, kind: 'news' | 'secondary'): Promise<string> {
    const params = new URLSearchParams();
    params.set('kind', kind);
    const query = params.toString();
    const { data } = await api.get<{ url: string }>(
      `/contentstore/items/${encodeURIComponent(itemId)}/url${query ? `?${query}` : ''}`
    );
    return data.url;
  }

  getAssetUrl(assetId: string, opts?: AssetUrlOptions): string {
    // Build asset URL with optional thumbnail params. Defaults to thumbnail for non-full contexts.
    const base = `/api/nexus/assets/${assetId}`;
    if (opts?.full) return base;
    const w = opts?.w ?? DEFAULT_THUMB_WIDTH;
    const q = opts?.q ?? DEFAULT_THUMB_QUALITY;
    const h = opts?.h;
    const fit = opts?.fit;
    const fmt = opts?.fmt;
    const params = new URLSearchParams();
    if (w) params.set('w', String(w));
    if (h) params.set('h', String(h));
    if (q) params.set('q', String(q));
    if (fit) params.set('fit', fit);
    if (fmt) params.set('fmt', fmt);
    const qs = params.toString();
    return qs ? `${base}?${qs}` : base;
  }

  async getUpdates(dimension_fqn?: string, page_number?: number): Promise<AtlasUpdate[]> {
    const params = new URLSearchParams();
    if (dimension_fqn) {
      params.append('dimension_fqn', dimension_fqn);
    }
    if (page_number !== undefined) {
      params.append('page_number', page_number.toString());
    }
    const { data } = await api.get<AtlasUpdate[]>(`/atlas/updates?${params.toString()}`);
    return data;
  }

  async getProfileByFqn(article_fqn: string): Promise<AtlasArticle> {
    try {
      const { data } = await api.get<AtlasArticle>(`/atlas/articles/${encodeURIComponent(article_fqn)}`);
      return data;
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  }

  async listArticlesBySubject(subject_fqn: string): Promise<{ fqn: string; context: AtlasContext }[]> {
    const params = new URLSearchParams({ subject_fqn });
    try {
      const { data } = await api.get<{ fqn: string; context: AtlasContext }[]>(`/atlas/articles?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  }

  async listArticlesByDimension(dimension_fqn: string): Promise<{
    fqn: string;
    name: string;
    subject_fqn: string;
    summary: string;
    hero_image_id?: string;
    context?: AtlasContext;
    latest_version_posthoc_tags?: AtlasPosthocTagMap;
    latest_version_posthoc_related_tags?: AtlasPosthocTagMap;
    date?: string;
  }[]> {
    const params = new URLSearchParams({ dimension_fqn });
    try {
      const { data } = await api.get<{
        fqn: string;
        name: string;
        subject_fqn: string;
        summary: string;
        hero_image_id?: string;
        context?: AtlasContext;
        latest_version_posthoc_tags?: AtlasPosthocTagMap;
        latest_version_posthoc_related_tags?: AtlasPosthocTagMap;
        date?: string;
      }[]>(`/atlas/articles?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  }

  async listArticlePosthocTagsByDimension(dimension_fqn: string): Promise<{
    fqn: string;
    latest_version_posthoc_tags?: AtlasPosthocTagMap;
    latest_version_posthoc_related_tags?: AtlasPosthocTagMap;
  }[]> {
    const params = new URLSearchParams({ dimension_fqn });
    try {
      const { data } = await api.get<{
        fqn: string;
        latest_version_posthoc_tags?: AtlasPosthocTagMap;
        latest_version_posthoc_related_tags?: AtlasPosthocTagMap;
      }[]>(`/atlas/articles/posthoc-tags?${params.toString()}`);
      return data;
    } catch (error) {
      console.error('API error:', error);
      throw error;
    }
  }

  async checkArticlesAvailable(): Promise<boolean> {
    try {
      const { data } = await api.get<{ fqn: string }[]>('/atlas/articles');
      return Array.isArray(data) && data.length > 0;
    } catch (error) {
      console.error('API error checking articles availability:', error);
      return false;
    }
  }

  async listAllArticleSummaries(): Promise<{ fqn: string; subject_fqn: string; dimension_fqn: string }[]> {
    try {
      const { data } = await api.get<{ fqn: string; subject_fqn: string; dimension_fqn: string }[]>('/atlas/articles');
      return data;
    } catch (error) {
      console.error('API error listing all article summaries:', error);
      return [];
    }
  }

  // --- Spark API Methods ---

  // Projects
  async getSparkProjects(): Promise<SparkProjectOverview[]> {
    const { data } = await api.get<SparkProjectOverview[]>('/spark/projects');
    return data;
  }

  async getSparkProject(projectFqn: string): Promise<SparkProjectDetails> {
    const { data } = await api.get<SparkProjectDetails>(`/spark/projects/${encodeURIComponent(projectFqn)}`);
    return data;
  }

  async closeSparkProject(projectFqn: string): Promise<SparkProjectDetails> {
    const { data } = await api.post<SparkProjectDetails>('/spark/projects/close', { project_fqn: projectFqn });
    return data;
  }

  async reopenSparkProject(projectFqn: string): Promise<SparkProjectDetails> {
    const { data } = await api.post<SparkProjectDetails>('/spark/projects/reopen', { project_fqn: projectFqn });
    return data;
  }

  async createSparkProject(data: { flow_fqn: string; name: string }): Promise<{ fqn: string }> {
    const { data: responseData } = await api.post<{ fqn: string }>('/spark/projects/create', data);
    return responseData;
  }

  async updateSparkProject(data: { project: Partial<SparkProjectDetails> & { fqn: string } }): Promise<SparkProjectDetails> {
    const { data: responseData } = await api.post<SparkProjectDetails>('/spark/projects/update', data);
    return responseData;
  }

  async deleteSparkProject(data: { project_fqn: string }): Promise<{ success: boolean }> {
    const { data: responseData } = await api.post<{ success: boolean }>('/spark/projects/delete', data);
    return responseData;
  }

  // Flows
  async getSparkFlows(): Promise<SparkFlow[]> {
    const { data } = await api.get<SparkFlow[]>('/spark/flows');
    return data;
  }

  async getSparkFlow(flowFqn: string): Promise<SparkFlow> {
    const { data } = await api.get<SparkFlow>(`/spark/flows/${encodeURIComponent(flowFqn)}`);
    return data;
  }

  // Creatives
  async getSparkCreatives(projectFqn: string, params?: { creative_type_fqn?: string; label?: string }): Promise<SparkCreative[]> {
    const { data } = await api.get<SparkCreative[]>(`/spark/creatives/project/${encodeURIComponent(projectFqn)}`, { params });
    return data;
  }
  
  async deleteSparkCreatives(data: { creative_fqns: string[] }): Promise<{ success: boolean }> {
    const { data: responseData } = await api.post<{ success: boolean }>('/spark/creatives/delete', data);
    return responseData;
  }

  async updateSparkCreative(data: { creative: Partial<SparkCreative> & { fqn: string } }): Promise<SparkCreative> {
    const { data: responseData } = await api.post<SparkCreative>('/spark/creatives/update', data);
    return responseData;
  }

  // Legacy creative publish/unpublish removed; use Library Item flow

  async addSparkLabel(data: { creative_fqns: string[], label: string }): Promise<{ success: boolean }> {
    const { data: responseData } = await api.post<{ success: boolean }>('/spark/labels/add', data);
    return responseData;
  }
  
  async removeSparkLabel(data: { creative_fqns: string[], label: string }): Promise<{ success: boolean }> {
    const { data: responseData } = await api.post<{ success: boolean }>('/spark/labels/remove', data);
    return responseData;
  }
  
  async getSparkProjectLabels(projectFqn: string): Promise<string[]> {
    const { data } = await api.get<{ labels: string[] }>(`/spark/labels/${encodeURIComponent(projectFqn)}`);
    return data.labels || []; // Return labels array or empty array if undefined
  }
  
  // Iterations & Images
  async getSparkIterations(creativeFqn: string): Promise<SparkIteration[]> {
    const { data } = await api.get<SparkIteration[]>(`/spark/iterations/creative/${encodeURIComponent(creativeFqn)}`);
    return data;
  }

  async getSparkIteration(iterationFqn: string): Promise<SparkIteration> {
    const { data } = await api.get<SparkIteration>(`/spark/iterations/${encodeURIComponent(iterationFqn)}`);
    return data;
  }

  async updateSparkIteration(data: { iteration: Partial<SparkIteration> & { fqn: string } }): Promise<SparkIteration> {
    const { data: responseData } = await api.post<SparkIteration>('/spark/iterations/update', data);
    return responseData;
  }

  // Creative Attachments
  async listCreativeAttachments(creativeFqn: string): Promise<SparkAttachment[]> {
    const { data } = await api.get<SparkAttachment[]>(`/spark/creatives/${encodeURIComponent(creativeFqn)}/attachments`);
    return data;
  }

  async uploadCreativeAttachment(creativeFqn: string, file: File): Promise<SparkAttachment> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const { data } = await api.post<SparkAttachment>(`/spark/creatives/${encodeURIComponent(creativeFqn)}/attachments/upload`, formData);
    return data;
  }

  async deleteCreativeAttachment(creativeFqn: string, fileId: string): Promise<{ file_id: string }> {
    const { data } = await api.post<{ file_id: string }>(`/spark/creatives/${encodeURIComponent(creativeFqn)}/attachments/delete`, { file_id: fileId });
    return data;
  }

  getCreativeAttachmentDownloadUrl(creativeFqn: string, fileId: string): string {
    return `/api/spark/creatives/${encodeURIComponent(creativeFqn)}/attachments/${encodeURIComponent(fileId)}`;
  }
  
  async selectSparkCurrentImage(iterationFqn: string, data: { element_id: string, file_ids: string[] }): Promise<SparkIteration> {
    const { data: responseData } = await api.post<SparkIteration>(`/spark/iterations/${encodeURIComponent(iterationFqn)}/images/select_current`, data);
    return responseData;
  }
  
  async deleteSparkImageAlternative(iterationFqn: string, data: { element_id: string, file_id: string }): Promise<SparkIteration> {
    const { data: responseData } = await api.post<SparkIteration>(
      `/spark/iterations/${encodeURIComponent(iterationFqn)}/images/delete`, 
      data
    );
    return responseData; // Assuming the backend returns the updated iteration
  }
  
  async uploadSparkImageAlternative(iterationFqn: string, elementId: string, file: File): Promise<SparkIteration> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post<SparkIteration>(
      `/spark/iterations/${encodeURIComponent(iterationFqn)}/images/upload?element_id=${encodeURIComponent(elementId)}`,
      formData
    );
    return data;
  }

  /**
   * Set the comment for a media element within an iteration.
   */
  async setSparkIterationImageComment(
    iterationFqn: string,
    elementId: string,
    fileId: string,
    comment: string
  ): Promise<SparkIteration> {
    const payload = {
      element_id: elementId,
      file_id: fileId,
      comment,
    };
    const { data: responseData } = await api.post<SparkIteration>(
      `/spark/iterations/${encodeURIComponent(iterationFqn)}/images/comment`,
      payload
    );
    return responseData;
  }

  async downloadSparkIterationExport(iterationFqn: string, format: 'pptx' | 'docx'): Promise<Blob> {
    const response = await api.get<Blob>(`/spark/iterations/${encodeURIComponent(iterationFqn)}/export?format=${format}`, {
      responseType: 'blob',
    });
    return response.data;
  }

  // Feedback
  async addSparkFeedback(iterationFqn: string, data: { content: string; metadata?: Record<string, any>; is_comment?: boolean }): Promise<SparkIteration> {
    const { data: responseData } = await api.post<SparkIteration>('/spark/iterations/feedback/create', { iteration_fqn: iterationFqn, ...data });
    return responseData;
  }

  async updateSparkFeedback(iterationFqn: string, feedbackId: string, data: { content: string }): Promise<SparkIteration> {
    const { data: responseData } = await api.post<SparkIteration>('/spark/iterations/feedback/update', { iteration_fqn: iterationFqn, feedback_id: feedbackId, ...data });
    return responseData;
  }

  async deleteSparkFeedback(iterationFqn: string, feedbackId: string): Promise<SparkIteration> {
    const { data: responseData } = await api.post<SparkIteration>('/spark/iterations/feedback/delete', { iteration_fqn: iterationFqn, feedback_id: feedbackId });
    return responseData;
  }

  // Jobs & Actions
  async getSparkJobs(projectFqn: string, params?: { status?: string }): Promise<SparkJob[]> {
    const { data } = await api.get<SparkJob[]>(`/spark/jobs/project/${encodeURIComponent(projectFqn)}`, { params });
    return data;
  }

  async getSparkJob(jobFqn: string): Promise<SparkJob> {
    const { data } = await api.get<SparkJob>(`/spark/jobs/${encodeURIComponent(jobFqn)}`);
    return data;
  }

  async getMySparkJobs(params?: { status?: string }): Promise<SparkJob[]> {
    const { data } = await api.get<SparkJob[]>(`/spark/jobs/my`, { params });
    return data;
  }

  async getSparkProjectCompletedJobCount(projectFqn: string): Promise<{ completed_job_count: number; worker_is_online: boolean }> {
    const { data } = await api.get<{ completed_job_count: number; worker_is_online: boolean }>(`/spark/jobs/project/${encodeURIComponent(projectFqn)}/completed_job_count`);
    return data;
  }

  /**
   * Mark one or more Spark jobs as seen by the current user.
   */
  async markSparkJobAsSeen(data: { job_fqns: string[] }): Promise<{ success: boolean }> {
    const { data: responseData } = await api.post<{ success: boolean }>('/spark/jobs/mark_as_seen', data);
    return responseData;
  }

  async cancelSparkJob(jobFqn: string): Promise<SparkJob> {
    const { data: responseData } = await api.post<SparkJob>(`/spark/jobs/${encodeURIComponent(jobFqn)}/cancel`);
    return responseData;
  }

  async triggerSparkAction(data: {
    project_fqn?: string | null;
    library_item_fqn?: string | null;
    action_name: string;
    action_args: any;
  }): Promise<SparkJob> {
    const { data: responseData } = await api.post<SparkJob>('/spark/jobs/create', data);
    return responseData;
  }
  
  // Resources
  async uploadSparkResource(projectFqn: string, file: File): Promise<SparkProjectDetails> {
    const formData = new FormData();
    // Append the file itself
    formData.append('file', file, file.name);
    // Append the project FQN as a form field
    formData.append('project_fqn', projectFqn);

    // Let the browser/axios set the correct multipart boundary by **not** manually setting the Content-Type header.
    const { data } = await api.post<SparkProjectDetails>(
        '/spark/projects/resources/upload',
        formData
    );
    return data;
  }

  async startSparkDeepResearch(data: { project_fqn: string }): Promise<SparkProjectDetails> {
    const { data: responseData } = await api.post<SparkProjectDetails>('/spark/projects/resources/deep_research', data);
    return responseData;
  }
  
  /**
   * Suggest Spark resources based on project briefing.
   */
  async suggestSparkResources(
    projectFqn: string
  ): Promise<{ suggested_resources: SparkSuggestedResource[] }> {
    const { data } = await api.post<{
      suggested_resources: SparkSuggestedResource[];
    }>(
      '/spark/projects/resources/suggest',
      { project_fqn: projectFqn }
    );
    return data;
  }
  
  async suggestSparkDeepResearchBriefing(projectFqn: string): Promise<{ briefing: string }> {
    const { data } = await api.post<{ briefing: string }>(
      '/spark/projects/resources/deep_research/suggest',
      { project_fqn: projectFqn } 
    );
    return data;
  }
  
  async runSparkDeepResearch(projectFqn: string, briefing: string, webSearch: boolean): Promise<SparkProjectDetails> {
    const { data } = await api.post<SparkProjectDetails>(
      '/spark/projects/resources/deep_research/run',
      {
        project_fqn: projectFqn,
        briefing: briefing,
        web_search: webSearch
      }
    );
    return data;
  }

  async generateFSQ(projectFqn: string): Promise<{ focus_search_questions: FocusSearchQuestion[] }> {
    const { data } = await api.post<{ focus_search_questions: FocusSearchQuestion[] }>(
      '/spark/projects/resources/fsq/generate',
      { project_fqn: projectFqn }
    );
    return data;
  }

  async runFSQResearch(
    projectFqn: string,
    fsqs: FocusSearchQuestion[],
    webSearch: boolean
  ): Promise<{ project: SparkProjectDetails; jobs_created: number }> {
    const { data } = await api.post<{ project: SparkProjectDetails; jobs_created: number }>(
      '/spark/projects/resources/fsq/run',
      {
        project_fqn: projectFqn,
        focus_search_questions: fsqs,
        web_search: webSearch,
      }
    );
    return data;
  }

  async addSparkResource(data: { project_fqn: string, resource_fqn: string, comment: string | null }): Promise<SparkProjectDetails> {
    const { data: responseData } = await api.post<SparkProjectDetails>('/spark/projects/resources/add', data);
    return responseData;
  }
  
  async removeSparkResource(projectFqn: string, identifiers: { resourceFqn: string | null, jobFqn: string | null }): Promise<SparkProjectDetails> {
    const payload: { project_fqn: string, resource_fqn?: string, job_fqn?: string } = { project_fqn: projectFqn };
    // Prefer resourceFqn if both are present (backend likely handles this too)
    if (identifiers.resourceFqn) {
        payload.resource_fqn = identifiers.resourceFqn;
    } else if (identifiers.jobFqn) {
        payload.job_fqn = identifiers.jobFqn;
    }

    const { data: responseData } = await api.post<SparkProjectDetails>('/spark/projects/resources/remove', payload);
    return responseData;
  }

  // Bedrock (for resource content)
  async getBedrockResourceContent(resourceFqn: string): Promise<string[]> { // Returns array of page strings
    const { data } = await api.get<string[]>(`/nexus/bedrock/resources/${encodeURIComponent(resourceFqn)}/content`);
    return data;
  }

  // SPARK Users / General Users
  async listUsers(): Promise<User[]> {
    const { data } = await api.get<User[]>('/users/list');
    return data;
  }

  async addUserToSparkProject(projectFqn: string, username: string): Promise<SparkProjectDetails> {
    const { data } = await api.post<SparkProjectDetails>('/spark/projects/users/add', {
      project_fqn: projectFqn,
      username: username,
    });
    return data; // Return updated project details
  }

  async removeUserFromSparkProject(projectFqn: string, username: string): Promise<SparkProjectDetails> {
    const { data } = await api.post<SparkProjectDetails>('/spark/projects/users/remove', {
      project_fqn: projectFqn,
      username: username,
    });
    return data; // Return updated project details
  }

  // Spark Creative Types
  async getSparkCreativeTypes(): Promise<SparkCreativeType[]> {
    try {
      const response = await api.get<SparkCreativeType[]>('/spark/creative_types');
      return response.data;
    } catch (error) {
      console.error('Failed to fetch Spark creative types:', error);
      throw error;
    }
  }

  async getSparkCitationIndex(projectFqn: string): Promise<CitationIndex> {
    const { data } = await api.get<CitationIndex>(`/spark/projects/${encodeURIComponent(projectFqn)}/citation_index`);
    return data;
  }

  /**
   * Bulk resolve reference codes to resource metadata.
   * Used for dynamically resolving citations not in the project bibliography.
   * Returns null for references that don't exist.
   */
  async resolveReferences(references: string[]): Promise<Record<string, BibliographyEntry | null>> {
    const { data } = await api.post<Record<string, BibliographyEntry | null>>('/references/resolve', { references });
    return data;
  }


  // --- Spark Library Items (Clean Cut) ---

  // Items: CRUD
  async createLibraryItem(payload: {
    creative_type_fqn: string;
    title?: string;
    description?: string;
    labels?: string[];
    tag_pattern?: any[];
    elements_definition?: Record<string, any>;
    forms_overlay?: SparkFormOverlayEntry[];
    values?: Record<string, any>;
    images?: Record<string, any>;
  }): Promise<SparkLibraryItem> {
    const { data } = await api.post<SparkLibraryItem>(`/spark/library/items`, payload);
    return data;
  }

  async getLibraryItem(itemFqn: string): Promise<SparkLibraryItem> {
    const { data } = await api.get<SparkLibraryItem>(`/spark/library/items/${encodeURIComponent(itemFqn)}`);
    return data;
  }

  async updateLibraryItem(itemFqn: string, payload: Partial<Pick<SparkLibraryItem, 'title' | 'description' | 'labels' | 'values'>> & { tag_pattern?: any[] }): Promise<SparkLibraryItem> {
    const { data } = await api.post<SparkLibraryItem>(`/spark/library/items/${encodeURIComponent(itemFqn)}`, payload);
    return data;
  }

  async deleteLibraryItem(itemFqn: string): Promise<{ success: boolean }> {
    const { data } = await api.delete<{ success: boolean }>(`/spark/library/items/${encodeURIComponent(itemFqn)}`);
    return data;
  }

  // Items: lifecycle
  async publishLibraryItem(itemFqn: string): Promise<SparkLibraryItem> {
    const { data } = await api.post<SparkLibraryItem>(`/spark/library/items/${encodeURIComponent(itemFqn)}/publish`, {});
    return data;
  }

  async unpublishLibraryItem(itemFqn: string): Promise<SparkLibraryItem> {
    const { data } = await api.post<SparkLibraryItem>(`/spark/library/items/${encodeURIComponent(itemFqn)}/unpublish`, {});
    return data;
  }

  // Items: create from iteration
  async createLibraryItemFromIteration(iterationFqn: string): Promise<SparkLibraryItem> {
    const { data } = await api.post<SparkLibraryItem>(`/spark/library/from_iteration`, { iteration_fqn: iterationFqn });
    return data;
  }

  // Items: search & facets
  async searchLibraryItems(params: { q?: string; tags?: string[]; page?: number; page_size?: number; sort?: 'name' | 'publishedAt'; dir?: 'asc' | 'desc'; creative_type_fqn?: string; include_drafts?: boolean; state?: 'draft' | 'published'; owner?: string }): Promise<SparkLibrarySearchResponse> {
    const query = new URLSearchParams();
    if (params?.q) query.append('q', params.q);
    if (params?.tags) params.tags.forEach(tag => query.append('tags', tag));
    if (params?.page) query.append('page', String(params.page));
    if (params?.page_size) query.append('page_size', String(params.page_size));
    if (params?.sort) query.append('sort', params.sort);
    if (params?.dir) query.append('dir', params.dir);
    if (params?.creative_type_fqn) query.append('creative_type_fqn', params.creative_type_fqn);
    if (params?.include_drafts !== undefined) query.append('include_drafts', String(params.include_drafts));
    if (params?.state) query.append('state', params.state);
    if (params?.owner) query.append('owner', params.owner);
    const { data } = await api.get<SparkLibrarySearchResponse>(`/spark/library/search?${query.toString()}`);
    return data;
  }

  async fetchLibraryFacets(params: { q?: string; tags?: string[]; creative_type_fqn?: string; include_drafts?: boolean; state?: 'draft' | 'published'; owner?: string }): Promise<{ dimensions: TagFacetDimension[] }> {
    const query = new URLSearchParams();
    if (params?.q) query.append('q', params.q);
    if (params?.tags) params.tags.forEach(tag => query.append('tags', tag));
    if (params?.creative_type_fqn) query.append('creative_type_fqn', params.creative_type_fqn);
    if (params?.include_drafts !== undefined) query.append('include_drafts', String(params.include_drafts));
    if (params?.state) query.append('state', params.state);
    if (params?.owner) query.append('owner', params.owner);
    const { data } = await api.get<{ dimensions: TagFacetDimension[] }>(`/spark/library/facets?${query.toString()}`);
    return data;
  }

  // Items: exports
  async getLibraryExportFormats(itemFqn: string): Promise<{ supported_formats: string[] }> {
    const { data } = await api.get<{ supported_formats: string[] }>(`/spark/library/items/${encodeURIComponent(itemFqn)}/export/formats`);
    return data;
  }

  // Convenience URL helpers for HTML/PPTX
  getLibraryHtmlUrl(itemFqn: string): string {
    return `/api/spark/library/items/${encodeURIComponent(itemFqn)}/exports/html`;
  }

  getLibraryPptxUrl(itemFqn: string): string {
    return `/api/spark/library/items/${encodeURIComponent(itemFqn)}/export?format=pptx`;
  }

  getLibraryExportAssetUrl(itemFqn: string, exportId: string, filename: string): string {
    return `/api/spark/library/items/${encodeURIComponent(itemFqn)}/exports/${encodeURIComponent(exportId)}/files/${encodeURIComponent(filename)}`;
  }

  // Items: media (draft only)
  async uploadLibraryItemImageAlternative(itemFqn: string, elementId: string, file: File): Promise<SparkLibraryItem> {
    const formData = new FormData();
    formData.append('file', file);
    const { data } = await api.post<SparkLibraryItem>(
      `/spark/library/items/${encodeURIComponent(itemFqn)}/images/upload?element_id=${encodeURIComponent(elementId)}`,
      formData
    );
    return data;
  }

  async selectLibraryItemCurrentImage(itemFqn: string, dataPayload: { element_id: string; file_ids: string[] }): Promise<SparkLibraryItem> {
    const { data } = await api.post<SparkLibraryItem>(`/spark/library/items/${encodeURIComponent(itemFqn)}/images/select_current`, dataPayload);
    return data;
  }

  async deleteLibraryItemImageAlternative(itemFqn: string, dataPayload: { element_id: string; file_id: string }): Promise<SparkLibraryItem> {
    const { data } = await api.post<SparkLibraryItem>(`/spark/library/items/${encodeURIComponent(itemFqn)}/images/delete`, dataPayload);
    return data;
  }

  async setLibraryItemImageComment(itemFqn: string, elementId: string, fileId: string, comment: string): Promise<SparkLibraryItem> {
    const payload = { element_id: elementId, file_id: fileId, comment };
    const { data } = await api.post<SparkLibraryItem>(`/spark/library/items/${encodeURIComponent(itemFqn)}/images/comment`, payload);
    return data;
  }

  // Items: attachments (draft only)
  async listLibraryItemAttachments(itemFqn: string): Promise<SparkAttachment[]> {
    const { data } = await api.get<SparkAttachment[]>(`/spark/library/items/${encodeURIComponent(itemFqn)}/attachments`);
    return data;
  }

  async uploadLibraryItemAttachment(itemFqn: string, file: File): Promise<SparkAttachment> {
    const formData = new FormData();
    formData.append('file', file, file.name);
    const { data } = await api.post<SparkAttachment>(`/spark/library/items/${encodeURIComponent(itemFqn)}/attachments/upload`, formData);
    return data;
  }

  async deleteLibraryItemAttachment(itemFqn: string, fileId: string): Promise<{ file_id: string }> {
    const { data } = await api.post<{ file_id: string }>(`/spark/library/items/${encodeURIComponent(itemFqn)}/attachments/remove`, { file_id: fileId });
    return data;
  }

  getLibraryItemAttachmentDownloadUrl(itemFqn: string, fileId: string): string {
    // If we expose download via assets id, use /api/nexus/assets/<id>. Keeping route for parity with creative attachments style if added server-side later.
    return `/api/nexus/assets/${encodeURIComponent(fileId)}`;
  }

  // Library Import: Bulk upload via Excel
  getLibraryImportTemplateUrl(creativeTypeFqn: string): string {
    return `/api/spark/library/import/template?creative_type_fqn=${encodeURIComponent(creativeTypeFqn)}`;
  }

  async validateLibraryImport(file: File, creativeTypeFqn: string): Promise<ImportPreviewResponse> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('creative_type_fqn', creativeTypeFqn);
    const { data } = await api.post<ImportPreviewResponse>('/spark/library/import/validate', formData);
    return data;
  }

  async commitLibraryImport(previewId: string): Promise<ImportCommitResponse> {
    const { data } = await api.post<ImportCommitResponse>('/spark/library/import/commit', {
      preview_id: previewId,
      skip_rows_with_errors: true,
    });
    return data;
  }

  // PDF Import: Bulk upload via PDF extraction (async job)
  async startLibraryPdfImport(file: File, creativeTypeFqn: string): Promise<SparkJob> {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('creative_type_fqn', creativeTypeFqn);
    const { data } = await api.post<SparkJob>('/spark/library/import/pdf', formData);
    return data;
  }
}

export const atlasClient = new AtlasClient();

export default api; 
